// ═══════════════════════════════════════════════════════════
// Модалки дій над задачею: закрити з результатом, перенести дедлайн,
// скасувати з причиною, позначити «блокує хтось».
//
// Кожна відповідає одному серверному правилу і показує його людині ДО
// відправки: сервер усе одно перевірить, але форма, яка дає заповнити все
// й лише потім каже 422, — це форма, якою перестають користуватись.
// ═══════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiFetch } from '../../services/api';
import { useTaskUsers } from './TaskForm';
import {
  OUTCOMES_BY_TYPE, OUTCOME_LABEL, RESCHEDULE_REASONS, CANCEL_REASONS, LOSS_REASONS,
  DAY, pad, toDateInput, fmtDue, fmtWhen,
} from './taskVocab';

/**
 * Блокування — стан, не статус: задача лишається «в роботі» і лишається
 * простроченою, просто видно, через кого. Стандарт очікуваної дати —
 * 48 годин (той самий, що на сервері): ТЗ каже «ескалація через 48 годин»,
 * у нас це підказка за замовчуванням, а не закон.
 */
const BLOCK_DEFAULT_MS = 48 * 60 * 60 * 1000;

export function BlockTaskModal({ task, onClose, onDone }) {
  const toast = useToast();
  const { currentUser } = useAuth();
  const users = useTaskUsers();
  const start = new Date(Date.now() + BLOCK_DEFAULT_MS);

  const [mode, setMode] = useState('user');           // 'user' | 'external'
  const [byUid, setByUid] = useState('');
  const [external, setExternal] = useState('');
  const [what, setWhat] = useState('');
  const [date, setDate] = useState(toDateInput(start.getTime()));
  const [time, setTime] = useState(`${pad(start.getHours())}:${pad(start.getMinutes())}`);
  const [busy, setBusy] = useState(false);

  const expectedAt = date ? new Date(`${date}T${time || '18:00'}`).getTime() : null;
  const whoOk = mode === 'user' ? !!byUid : external.trim().length > 0;
  const ready = whoOk && what.trim().length >= 3 && expectedAt;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiFetch('/api/tasks/block', {
        method: 'POST',
        body: {
          id: task.id,
          byUid: mode === 'user' ? byUid : undefined,
          external: mode === 'external' ? external.trim() : undefined,
          what: what.trim(),
          expectedAt,
        },
      });
      toast.success(mode === 'user' ? 'Колезі надіслано сповіщення' : 'Позначено');
      onDone(r.task);
    } catch (err) {
      toast.error(err?.body?.message || err?.message || 'Не вдалося позначити');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--task" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-sm">
        <div className="modal-header">
          <h2>Хто блокує задачу</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="tasks-form-row">
            <label className="tasks-check">
              <input type="radio" checked={mode === 'user'} onChange={() => setMode('user')} />
              <span>Колега</span>
            </label>
            <label className="tasks-check">
              <input type="radio" checked={mode === 'external'} onChange={() => setMode('external')} />
              <span>Зовнішній контрагент</span>
            </label>
          </div>

          {mode === 'user' ? (
            <div className="form-field">
              <label className="field-label">Хто</label>
              <select value={byUid} onChange={e => setByUid(e.target.value)}>
                <option value="">— оберіть —</option>
                {users.filter(u => u.id !== currentUser.uid).map(u => (
                  <option key={u.id} value={u.id}>{u.displayName || u.email}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-field">
              <label className="field-label">Хто</label>
              <input type="text" value={external} placeholder="Постачальник, лабораторія, орендодавець…"
                onChange={e => setExternal(e.target.value)} />
              <p className="tasks-hint">Сповіщення не надсилається — це позначка для команди.</p>
            </div>
          )}

          <div className="form-field">
            <label className="field-label">
              Що саме блокує <span className="tasks-req">обовʼязково</span>
            </label>
            <input type="text" value={what} placeholder="Чекаю прайс на нові абонементи"
              onChange={e => setWhat(e.target.value)} />
          </div>

          <div className="tasks-form-row">
            <div className="form-field">
              <label className="field-label">Очікую розблокування до</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="form-field">
              <label className="field-label">Час</label>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} />
            </div>
          </div>

          <div className="task-pay-empty">
            Задача лишається у своєму статусі й, якщо прострочена, — простроченою.
            Час у блокуванні рахується окремо.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Скасувати</button>
          <button className="btn btn-primary" disabled={busy || !ready} onClick={submit}>
            {busy ? 'Зачекайте…' : 'Позначити'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Перенесення дедлайну.
 *
 * Причина потрібна лише коли дата їде ВПЕРЕД: підтягнути ближче можна
 * вільно, бо на раніше не переносять, щоб сховати прострочення.
 *
 * Якщо переносить виконавець чужої задачі — це запит, і до підтвердження
 * задача лишається простроченою. Сервер вирішує це сам, тут лише
 * попереджаємо, щоб людина не думала, що дата вже змінилась.
 */
export function RescheduleModal({ task, onClose, onDone }) {
  const toast = useToast();
  const { currentUser, hasPermission } = useAuth();
  const due = task.dueAt ? new Date(task.dueAt) : new Date();

  const [date, setDate] = useState(toDateInput(due.getTime()));
  const [time, setTime] = useState(`${pad(due.getHours())}:${pad(due.getMinutes())}`);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const nextAt = date ? new Date(`${date}T${time || '18:00'}`).getTime() : null;
  const later = nextAt != null && (task.dueAt == null || nextAt > task.dueAt);

  // Та сама умова, що на сервері: постановник міняє своє очікування сам.
  const isOwner = task.createdBy?.uid === currentUser.uid;
  const willAsk = later && !isOwner && !hasPermission('tasks_edit_all');

  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiFetch('/api/tasks/reschedule', {
        method: 'POST',
        body: { id: task.id, dueAt: nextAt, reasonId: later ? reason : undefined, comment: comment || undefined },
      });
      toast.success(r.pending ? 'Запит надіслано постановнику' : 'Дедлайн змінено');
      onDone(r.task);
    } catch (err) {
      toast.error(err?.body?.message || err?.message || 'Не вдалося перенести');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--task" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-sm">
        <div className="modal-header">
          <h2>Перенести дедлайн</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="tasks-hint" style={{ marginBottom: 12 }}>
            Зараз: {task.dueAt ? fmtWhen(task.dueAt) : 'без дедлайну'}
          </p>
          <div className="tasks-form-row">
            <div className="form-field">
              <label className="field-label">Нова дата</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="form-field">
              <label className="field-label">Час</label>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} />
            </div>
          </div>

          {later && (
            <div className="form-field">
              <label className="field-label">
                Причина <span className="tasks-req">обовʼязково</span>
              </label>
              <select value={reason} onChange={e => setReason(e.target.value)}>
                <option value="">— оберіть —</option>
                {RESCHEDULE_REASONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}

          <div className="form-field">
            <label className="field-label">Коментар (необовʼязково)</label>
            <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} />
          </div>

          {willAsk && (
            <div className="task-pay-empty">
              Дедлайн ставив не ви — це буде запит. Доки постановник його не
              підтвердить, задача лишається простроченою.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Скасувати</button>
          <button className="btn btn-primary" disabled={busy || !nextAt || (later && !reason)} onClick={submit}>
            {busy ? 'Зачекайте…' : willAsk ? 'Надіслати запит' : 'Перенести'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Скасування. Причина обовʼязкова — інакше «скасовано» нічого не пояснює,
 *  а задача просто зникає з прострочених. */
export function CancelTaskModal({ task, onClose, onDone }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiFetch('/api/tasks/status', {
        method: 'POST',
        body: { id: task.id, status: 'cancelled', cancelReasonId: reason, comment: comment || undefined },
      });
      toast.success('Задачу скасовано');
      onDone(r.task);
    } catch (err) {
      toast.error(err?.body?.message || err?.message || 'Не вдалося скасувати');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--task" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-sm">
        <div className="modal-header">
          <h2>Скасувати задачу</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-field">
            <label className="field-label">
              Причина <span className="tasks-req">обовʼязково</span>
            </label>
            <select value={reason} onChange={e => setReason(e.target.value)}>
              <option value="">— оберіть —</option>
              {CANCEL_REASONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="field-label">Коментар (необовʼязково)</label>
            <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Назад</button>
          <button className="btn btn-danger" disabled={busy || !reason} onClick={submit}>
            {busy ? 'Зачекайте…' : 'Скасувати задачу'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Закриття задачі. Форма залежить від типу — бо питання «що сталось» у
 * дзвінка й у продажу різні.
 *
 * Для продажу сума НЕ вводиться, а обирається платіж із каси. Це не
 * зручність, а захист: якщо на цій сумі потім рахувати людей, введена
 * руками цифра поповзе вгору. Поки платежу немає — продаж не закривається,
 * і для цього стану є статус «Очікує клієнта».
 */
export function CompleteTaskModal({ task, onClose, onDone }) {
  const toast = useToast();
  const isSale = task.type === 'sale';
  const choices = OUTCOMES_BY_TYPE[task.type] || [];

  const [outcome, setOutcome] = useState(choices[0] || '');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const [payments, setPayments] = useState(null);   // null = ще вантажиться
  const [paymentId, setPaymentId] = useState(null);

  // «Передзвонити» — коли саме. Стандарт: завтра о 10:00.
  const tomorrow = new Date(Date.now() + DAY);
  const [nextDate, setNextDate] = useState(toDateInput(tomorrow.getTime()));
  const [nextTime, setNextTime] = useState('10:00');
  const nextContactAt = nextDate ? new Date(`${nextDate}T${nextTime || '10:00'}`).getTime() : null;

  const needsPayment = isSale && outcome === 'won';
  const needsReason = outcome === 'refused';
  const needsNext = outcome === 'call_back';

  // Платежі тягнемо лише коли вони справді потрібні: у більшості закриттів
  // це зайвий запит.
  useEffect(() => {
    if (!needsPayment || payments !== null) return;
    apiFetch(`/api/analytics/task-payments?taskId=${encodeURIComponent(task.id)}`)
      .then(r => {
        setPayments(r.payments || []);
        if (r.selectedId) setPaymentId(r.selectedId);
      })
      .catch(() => setPayments([]));
  }, [needsPayment, payments, task.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiFetch('/api/tasks/complete', {
        method: 'POST',
        body: {
          id: task.id,
          outcome: outcome || undefined,
          lossReasonId: needsReason ? (reason || undefined) : undefined,
          transactionId: needsPayment ? (paymentId || undefined) : undefined,
          nextContactAt: needsNext ? (nextContactAt || undefined) : undefined,
          outcomeNote: note || undefined,
        },
      });
      toast.success(r.followUp
        ? `Задачу закрито · створено #${r.followUp.docNumber || ''} на ${fmtDue(r.followUp.dueAt)}`
        : 'Задачу закрито');
      onDone(r.task, r.followUp || null);
    } catch (err) {
      toast.error(err?.body?.message || err?.message || 'Не вдалося закрити задачу');
    } finally {
      setBusy(false);
    }
  };

  const blocked = (needsPayment && !paymentId) || (needsReason && !reason) || (needsNext && !nextContactAt);

  return (
    <div className="modal-overlay modal-overlay--task" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <h2>Закрити задачу</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {choices.length > 0 && (
            <div className="form-field">
              <label className="field-label">Результат</label>
              <select value={outcome} onChange={e => setOutcome(e.target.value)}>
                {choices.map(k => <option key={k} value={k}>{OUTCOME_LABEL[k]}</option>)}
              </select>
            </div>
          )}

          {needsPayment && (
            <div className="form-field">
              <label className="field-label">
                Платіж у касі <span className="tasks-req">обовʼязково</span>
              </label>
              {payments === null ? (
                <div className="tasks-picker-note">Шукаю платежі клієнта…</div>
              ) : !payments.length ? (
                // Не помилка, а стан: гроші ще не пройшли. Тому підказуємо
                // статус, а не змушуємо вигадувати суму.
                <div className="task-pay-empty">
                  Платежів по цьому клієнту в касі немає. Продаж закривається, коли
                  гроші пройшли — до того лишіть задачу в статусі «Очікує клієнта».
                </div>
              ) : (
                <div className="task-pay-list">
                  {payments.map(p => (
                    <label
                      key={p.id}
                      className={`task-pay${paymentId === p.id ? ' active' : ''}${p.takenBy ? ' taken' : ''}`}
                    >
                      <input
                        type="radio" name="payment" value={p.id}
                        checked={paymentId === p.id} disabled={!!p.takenBy}
                        onChange={() => setPaymentId(p.id)}
                      />
                      <span className="task-pay-body">
                        <span className="task-pay-top">
                          <strong>{Number(p.amount).toLocaleString('uk-UA')} ₴</strong>
                          <span className="task-pay-date">{p.date}</span>
                        </span>
                        <span className="task-pay-sub">
                          {p.title}{p.method ? ` · ${p.method}` : ''}
                          {p.takenBy && ` · вже закриває «${p.takenBy.title}»`}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {needsNext && (
            <div className="form-field">
              <label className="field-label">
                Наступний контакт <span className="tasks-req">обовʼязково</span>
              </label>
              <div className="tasks-form-row">
                <input type="date" value={nextDate} min={toDateInput(Date.now())} onChange={e => setNextDate(e.target.value)} />
                <input type="time" value={nextTime} onChange={e => setNextTime(e.target.value)} />
              </div>
              <p className="tasks-hint">Цю задачу закриємо, а на вказаний час створимо нову «передзвонити».</p>
            </div>
          )}

          {needsReason && (
            <div className="form-field">
              <label className="field-label">
                Причина відмови <span className="tasks-req">обовʼязково</span>
              </label>
              <select value={reason} onChange={e => setReason(e.target.value)}>
                <option value="">— оберіть —</option>
                {LOSS_REASONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}

          <div className="form-field">
            <label className="field-label">Коментар (необовʼязково)</label>
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Скасувати</button>
          <button className="btn btn-primary" disabled={busy || blocked} onClick={submit}>
            {busy ? 'Зачекайте…' : 'Закрити задачу'}
          </button>
        </div>
      </div>
    </div>
  );
}
